import React from 'react'
import {
  FileStack,
  Plus,
  Bot,
  Wrench,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Trash2,
  FileText,
  Lock,
  Globe,
  User,
  Calendar,
  Pencil,
  Info,
  Zap,
  AlertCircle,
  ArrowLeft,
  Mail,
  Upload,
  Webhook,
  Link2,
  Unlink,
  RefreshCw,
  ChevronRight,
  Check,
  Shield,
  Users,
  MessageSquare,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Mockup: Template Card (Analyzer Ready - full details)
// ---------------------------------------------------------------------------
function TemplateCardMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Invoice Extraction</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Extract key fields from supplier invoices</p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
          <CheckCircle2 className="h-3 w-3" />
          Analyzer Ready
        </span>
      </div>

      {/* Source documents */}
      <div className="mt-3">
        <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
          <FileText className="h-3 w-3" />
          Built from 2 documents
        </p>
        <div className="flex flex-wrap gap-1.5">
          {['sample_invoice.pdf', 'invoice_template.pdf'].map((doc, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-900/25 text-[10px] font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50">
              <FileText className="h-2.5 w-2.5 shrink-0" />
              <span className="truncate max-w-[140px]">{doc}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Stats grid */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2.5">
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Fields</p>
          <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 mt-0.5">12</p>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2.5">
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Analyzer ID</p>
          <p className="text-[9px] font-mono text-gray-700 dark:text-gray-300 mt-0.5 truncate">invoice-v2</p>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2.5">
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Access</p>
          <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 mt-0.5 flex items-center gap-1">
            <Globe className="h-2.5 w-2.5 text-green-600 dark:text-green-400" />
            All users
          </p>
        </div>
      </div>

      {/* Metadata */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-gray-400 dark:text-gray-500">
        <span className="inline-flex items-center gap-1"><User className="h-2.5 w-2.5" />John Smith</span>
        <span className="inline-flex items-center gap-1"><Calendar className="h-2.5 w-2.5" />12 May 2026</span>
      </div>

      {/* Action buttons */}
      <div className="mt-3.5 flex flex-wrap gap-2">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-[11px] text-gray-700 dark:text-gray-200">
          Manage <ArrowRight className="h-3 w-3" />
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-300 dark:border-red-700 text-[11px] text-red-600 dark:text-red-400 ml-auto">
          <Trash2 className="h-3 w-3" /> Delete
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Template with "Build Needed" status (same details as Ready card)
// ---------------------------------------------------------------------------
function TemplateCardNoBuildMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Cat Bond Analyzer</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Extract catastrophe bond terms and conditions</p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
          <Wrench className="h-3 w-3" />
          Build Needed
        </span>
      </div>

      {/* Source documents */}
      <div className="mt-3">
        <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
          <FileText className="h-3 w-3" />
          Built from 2 documents
        </p>
        <div className="flex flex-wrap gap-1.5">
          {['cat_bond_2024.pdf', 'cat_bond_sample.pdf'].map((doc, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-900/25 text-[10px] font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50">
              <FileText className="h-2.5 w-2.5 shrink-0" />
              <span className="truncate max-w-[140px]">{doc}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2.5">
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Fields</p>
          <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 mt-0.5">8</p>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2.5">
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Analyzer ID</p>
          <p className="text-[9px] font-mono text-gray-500 dark:text-gray-400 mt-0.5">Not built yet</p>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2.5">
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Access</p>
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mt-0.5 flex items-center gap-1">
            <Lock className="h-2.5 w-2.5" />3 users
          </p>
        </div>
      </div>

      {/* Metadata */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-gray-400 dark:text-gray-500">
        <span className="inline-flex items-center gap-1"><User className="h-2.5 w-2.5" />Jane Doe</span>
        <span className="inline-flex items-center gap-1"><Calendar className="h-2.5 w-2.5" />10 May 2026</span>
      </div>

      <div className="mt-3.5 flex flex-wrap gap-2">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-[11px] text-gray-700 dark:text-gray-200">
          Manage <ArrowRight className="h-3 w-3" />
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-navy-800 text-white text-[11px] font-medium">
          <Bot className="h-3 w-3" /> Build Analyzer
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-300 dark:border-red-700 text-[11px] text-red-600 dark:text-red-400 ml-auto">
          <Trash2 className="h-3 w-3" /> Delete
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Templates List Page Header
// ---------------------------------------------------------------------------
function TemplatesPageHeaderMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Templates & Analyzers</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Create templates in the web app, then select them in the plugin during extraction.</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-8 px-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
            <span className="text-[10px] text-gray-500">All Templates ▾</span>
          </div>
          <div className="h-8 px-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
            <span className="text-[10px] text-gray-500">Last Modified ▾</span>
          </div>
          <div className="h-8 px-3 bg-navy-800 rounded-lg flex items-center gap-1.5">
            <Plus className="h-3 w-3 text-white" />
            <span className="text-[10px] text-white font-medium">New Template</span>
          </div>
        </div>
      </div>
      {/* Info banner */}
      <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 p-3 flex gap-2.5">
        <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-blue-800 dark:text-blue-200">
          <p className="font-medium">Using plugin and can't find the right analyzer?</p>
          <p className="mt-0.5 text-blue-700 dark:text-blue-300">Click "Create New Analyzer" in plugin, create a template, build the analyzer, then return to plugin and extract.</p>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Template Detail Page (Fields tab with header actions)
// ---------------------------------------------------------------------------
function TemplateDetailMockup() {
  const categories = [
    {
      name: 'ORDER DETAILS',
      fields: [
        { display: 'PO Number', field_name: 'PONumber', type: 'text', userInput: false, description: 'The unique purchase order identifier or reference number' },
        { display: 'Order Date', field_name: 'OrderDate', type: 'date', userInput: false, description: 'The date the purchase order was issued' },
        { display: 'Delivery Date', field_name: 'DeliveryDate', type: 'date', userInput: false, description: 'The expected delivery date for this order' },
        { display: 'Payment Terms', field_name: 'PaymentTerms', type: 'text', userInput: true, description: 'The agreed payment terms (e.g. Net 30, Net 60)' },
      ],
    },
    {
      name: 'VENDOR',
      fields: [
        { display: 'Vendor Name', field_name: 'VendorName', type: 'text', userInput: false, description: 'The name of the supplying company' },
        { display: 'Vendor Address', field_name: 'VendorAddress', type: 'text', userInput: false, description: 'The full address of the vendor' },
        { display: 'Vendor Tax ID', field_name: 'VendorTaxId', type: 'text', userInput: false, description: 'The tax identification number of the vendor' },
      ],
    },
  ]

  return (
    <div className="space-y-4">
      {/* Page header with actions */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] text-gray-400 mb-1">
              <ArrowLeft className="h-3 w-3" /> Back to Templates
            </div>
            <div className="text-sm font-bold text-gray-900 dark:text-gray-100">Purchase Order Extraction</div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Extract key fields from vendor purchase orders</div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <div className="h-7 px-2.5 flex items-center gap-1 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg">
              <Sparkles className="h-3 w-3 text-white" />
              <span className="text-[9px] text-white font-medium">AI Suggest Fields</span>
            </div>
            <div className="h-7 px-2.5 flex items-center gap-1 border border-gray-300 dark:border-gray-700 rounded-lg">
              <Plus className="h-3 w-3 text-gray-500" />
              <span className="text-[9px] text-gray-700 dark:text-gray-300">Add Field</span>
            </div>
            <div className="h-7 px-2.5 flex items-center gap-1 border border-blue-300 dark:border-blue-700 rounded-lg">
              <Upload className="h-3 w-3 text-blue-600" />
              <span className="text-[9px] text-blue-600 dark:text-blue-400">Import Fields</span>
            </div>
            <div className="h-7 px-2.5 flex items-center gap-1 border border-red-300 dark:border-red-700 rounded-lg">
              <Trash2 className="h-3 w-3 text-red-600" />
              <span className="text-[9px] text-red-600 dark:text-red-400">Delete All Fields</span>
            </div>
            <div className="h-7 px-2.5 flex items-center gap-1 bg-navy-800 rounded-lg">
              <Bot className="h-3 w-3 text-white" />
              <span className="text-[9px] text-white font-medium">Build Analyzer</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1.5 inline-flex gap-1">
        <div className="px-3 py-1.5 rounded-lg bg-navy-800 text-white text-[10px] font-medium">Fields</div>
        <div className="px-3 py-1.5 rounded-lg text-[10px] text-gray-600 dark:text-gray-300">Webhooks</div>
        <div className="px-3 py-1.5 rounded-lg text-[10px] text-gray-600 dark:text-gray-300">Settings</div>
      </div>

      {/* Fields table */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <span className="text-xs font-bold text-gray-900 dark:text-gray-100">Fields</span>
        </div>
        {/* Categories with tables */}
        <div className="p-4 space-y-5">
          {categories.map((cat, ci) => (
            <div key={ci}>
              {/* Category header */}
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{cat.name}</p>
              {/* Table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
                <table className="min-w-full text-[11px]">
                  <thead className="bg-gray-50 dark:bg-gray-800/60">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Display</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Field Name</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Type</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-500 dark:text-gray-400">User Input</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Description</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.fields.map((field, i) => (
                      <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium whitespace-nowrap">{field.display}</td>
                        <td className="px-3 py-2 font-mono text-[10px] text-gray-600 dark:text-gray-400">{field.field_name}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{field.type}</td>
                        <td className="px-3 py-2 text-center">
                          <div className={`relative inline-flex h-4 w-8 items-center rounded-full ${field.userInput ? 'bg-blue-600' : 'bg-red-400'}`}>
                            <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform ${field.userInput ? 'translate-x-[17px]' : 'translate-x-[3px]'}`} />
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-xs truncate">{field.description}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-1">
                            <Pencil className="h-3.5 w-3.5 text-gray-400" />
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Create Template Modal (left aligned)
// ---------------------------------------------------------------------------
function CreateModalMockup() {
  return (
    <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-6">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Create Template</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Define what fields you want extracted, then build a custom analyzer from those fields.</p>

      <div className="mt-5 space-y-4">
        {/* Template name */}
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Template name</label>
          <input
            type="text"
            readOnly
            value="Invoice Extraction"
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Description</label>
          <textarea
            readOnly
            rows={3}
            placeholder="What kind of document is this template for?"
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm resize-none dark:placeholder-gray-500"
          />
        </div>

        {/* Reprocessing toggle */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <div className="flex items-start gap-3">
            <div className="relative inline-flex h-4 w-7 shrink-0 rounded-full border-2 border-transparent bg-[var(--token-success,#3b9b5e)] mt-0.5">
              <span className="inline-block h-3 w-3 rounded-full bg-white shadow transform translate-x-3" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Allow reprocessing duplicates</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Turn off to hard-block uploading the same file again for this template in the plugin.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer buttons */}
      <div className="mt-5 flex justify-end gap-3">
        <button className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200">Cancel</button>
        <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium">
          Create and Continue
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Settings Tab
// ---------------------------------------------------------------------------
function SettingsTabMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-4">
      {/* Info grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Created by', value: 'John Smith', icon: User, color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
          { label: 'Created', value: '12 May 2026', icon: Calendar, color: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
          { label: 'Last updated', value: '20 May 2026', icon: RefreshCw, color: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' },
          { label: 'Method', value: 'AI Suggested', icon: Sparkles, color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' },
        ].map((item, i) => {
          const Icon = item.icon
          return (
            <div key={i} className="flex items-start gap-2">
              <div className={`p-1.5 rounded-md ${item.color}`}>
                <Icon className="h-3 w-3" />
              </div>
              <div>
                <p className="text-[9px] text-gray-500 dark:text-gray-400">{item.label}</p>
                <p className="text-[10px] font-medium text-gray-900 dark:text-gray-100 mt-0.5">{item.value}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Source Documents */}
      <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-1.5 mb-2">
          <FileText className="h-3 w-3 text-blue-500" />
          <p className="text-[9px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Source Documents</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {['sample_invoice.pdf', 'invoice_template.pdf'].map((doc, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/25 text-[10px] font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50">
              <FileText className="h-3 w-3 shrink-0" />
              {doc}
            </span>
          ))}
        </div>
      </div>

      {/* Duplicate Handling toggle */}
      <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Duplicate Handling</p>
            <p className="text-[11px] text-gray-700 dark:text-gray-300 mt-0.5">Duplicates allowed for this template</p>
          </div>
          <div className="w-7 h-4 rounded-full bg-[var(--token-success,#3b9b5e)] relative flex-shrink-0">
            <div className="absolute top-0.5 right-0.5 w-3 h-3 rounded-full bg-white shadow-sm" />
          </div>
        </div>
        <p className="text-[9px] text-gray-500 dark:text-gray-400 mt-1">Turn off to prevent re-uploading the same file hash for this template in the plugin.</p>
      </div>

      {/* Mandate Review toggle */}
      <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Mandate Review Before Export</p>
            <p className="text-[11px] text-gray-700 dark:text-gray-300 mt-0.5">Requests require manual review; user must publish to trigger CSV export</p>
          </div>
          <div className="w-7 h-4 rounded-full bg-[var(--token-success,#3b9b5e)] relative flex-shrink-0">
            <div className="absolute top-0.5 right-0.5 w-3 h-3 rounded-full bg-white shadow-sm" />
          </div>
        </div>
        <p className="text-[9px] text-gray-500 dark:text-gray-400 mt-1">When enabled, requests go to the queue for manual review. When disabled, extraction auto-approves and fires the webhook immediately.</p>
      </div>

      {/* Access Control */}
      <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-1.5 mb-2">
          <Lock className="h-3 w-3 text-gray-500" />
          <p className="text-[10px] font-semibold text-gray-600 dark:text-gray-300">Manage Access</p>
        </div>
        <p className="text-[10px] text-gray-500 dark:text-gray-400">Control which users can see and use this template in the Outlook plugin. All users have access by default.</p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Webhooks Tab
// ---------------------------------------------------------------------------
function WebhooksTabMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-xs font-semibold text-gray-900 dark:text-gray-100">Template Webhooks</h2>
          <p className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5">Route approved output from this template to linked webhook endpoints.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-7 px-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
            <span className="text-[9px] text-gray-400">Select webhook to connect</span>
          </div>
          <div className="h-7 px-2.5 bg-navy-800 rounded-lg flex items-center gap-1">
            <Link2 className="h-3 w-3 text-white" />
            <span className="text-[9px] text-white font-medium">Connect</span>
          </div>
        </div>
      </div>
      {/* Linked webhook example */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800/40 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Webhook className="h-3.5 w-3.5 text-teal-500" />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-semibold text-gray-900 dark:text-gray-100">CSV Export Webhook</span>
                <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium">Active</span>
              </div>
              <p className="text-[9px] text-gray-400 font-mono truncate mt-0.5">https://api.example.com/webhook/export</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-6 px-2 flex items-center gap-1 border border-gray-300 dark:border-gray-700 rounded-lg text-[9px]">
              <ChevronRight className="h-3 w-3" /> Deliveries
            </div>
            <div className="h-6 px-2 flex items-center gap-1 bg-red-600 text-white rounded-lg text-[9px]">
              <Unlink className="h-3 w-3" /> Disconnect
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Templates Guide Content
// ---------------------------------------------------------------------------
export default function TemplatesGuide({ onNavigate }) {
  return (
    <div className="space-y-10">
      {/* Module Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <FileStack className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Templates & Analyzers</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Define extraction schemas, build AI analyzers, and manage field configurations for document processing.</p>
        </div>
      </div>

      {/* Section 1: Overview & Page Header */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">1</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Templates List Page</h3>
        </div>
        <TemplatesPageHeaderMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>The Templates page shows all your extraction templates in a card grid layout. Key controls:</p>
          <p>• <strong>Filter dropdown</strong> filter by "All Templates", "Analyzer Ready", or "No Analyzer" to quickly find templates needing attention.</p>
          <p>• <strong>Sort dropdown</strong> sort by "Last Modified", "Name (A-Z)", or "Date Created".</p>
          <p>• <strong>New Template</strong> button opens the create template modal to start a new extraction schema.</p>
          <p>• <strong>Info banner</strong> reminds Outlook Plugin users that they can create templates here when they can't find the right analyzer in the plugin.</p>
        </div>
      </section>

      {/* Section 2: Template Cards */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">2</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Template Cards</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">Templates are displayed as cards in a 2-column grid (1 column on mobile). Each card contains:</p>
        
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold text-green-700 dark:text-green-400">✓ Analyzer Ready</p>
            <TemplateCardMockup />
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">âš  Build Needed</p>
            <TemplateCardNoBuildMockup />
          </div>
        </div>

        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mt-4">
          <p>Each template card shows:</p>
          <p>• <strong>Name & Description</strong> template title and a short description of its purpose.</p>
          <p>• <strong>Status Badge</strong> Green "Analyzer Ready" means it's built and usable in the plugin. Amber "Build Needed" means you need to build the analyzer first.</p>
          <p>• <strong>AI Job Status</strong> if AI field suggestion is running, a purple badge shows progress. When complete, a green "AI Fields Ready - Review" link appears.</p>
          <p>• <strong>Source Documents</strong> shows which documents the template was built from (blue pills with file icons). Shows the creation method if created manually.</p>
          <p>• <strong>Stats Grid</strong> three metrics: number of fields, Azure Analyzer ID (or "Not built yet"), and access level (All users / N users).</p>
          <p>• <strong>Metadata</strong> creator name and creation date.</p>
          <p>• <strong>Actions</strong>:</p>
          <div className="ml-4 space-y-1">
            <p>- <strong>Manage</strong> opens the Template Detail page to edit fields, prompts, and settings.</p>
            <p>- <strong>Build Analyzer</strong> one-click build (only shown when no analyzer exists and fields are defined).</p>
            <p>- <strong>Delete</strong> removes the template (with confirmation).</p>
          </div>
        </div>
      </section>

      {/* Section 3: Creating a Template */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">3</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Creating a New Template</h3>
        </div>
        <CreateModalMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mt-4">
          <p>Click <strong>"New Template"</strong> to open the creation modal. You'll need to provide:</p>
          <p>• <strong>Template Name</strong> (required) a descriptive name like "Invoice Extraction" or "Purchase Order".</p>
          <p>• <strong>Description</strong> (optional) helps team members understand what document type this template is for.</p>
          <p>• <strong>Allow Reprocessing Duplicates</strong> toggle ON (default) to allow the same file to be uploaded multiple times. Toggle OFF to hard-block duplicate uploads in the Outlook plugin for this template.</p>
          <p className="mt-2">After clicking <strong>"Create and Continue"</strong>, you'll be redirected to the Template Detail page to add fields.</p>
        </div>
      </section>

      {/* Section 4: Template Detail Page */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">4</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Template Detail Page (Managing Fields)</h3>
        </div>
        <TemplateDetailMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mt-4">
          <p>The Template Detail page is where you configure exactly what data gets extracted. It has three tabs: <strong>Fields</strong>, <strong>Webhooks</strong>, and <strong>Settings</strong>.</p>
          
          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Header Action Buttons (Fields Tab)</p>
          <div className="ml-4 space-y-1">
            <p>- <strong>AI Suggest Fields</strong> (purple gradient) upload sample PDFs and let AI analyze them to suggest extraction fields.</p>
            <p>- <strong>Add Field</strong> manually add a new extraction field via modal.</p>
            <p>- <strong>Import Fields</strong> import fields from a JSON file (supports Azure CU analyzer format, flat dict, or direct dict).</p>
            <p>- <strong>Delete All Fields</strong> (red) removes ALL fields with confirmation. Cannot be undone.</p>
            <p>- <strong>Build Analyzer</strong> (navy) creates/rebuilds the Azure Document Intelligence analyzer from your current fields.</p>
          </div>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Adding Fields</p>
          <p>• Click <strong>"Add Field"</strong> to open the field editor modal with these inputs:</p>
          <div className="ml-4 space-y-1">
            <p>- <strong>Display Name</strong> shown in UI (e.g. "Invoice Number")</p>
            <p>- <strong>Field Name</strong> (internal API key, auto-generated from display name with "Auto" button)</p>
            <p>- <strong>Data Type</strong> select from supported types</p>
            <p>- <strong>Category</strong> free-text with autocomplete from existing categories (e.g. "Line Items", "Buyer Details")</p>
            <p>- <strong>Description</strong> guides the AI on what to extract (the more specific, the better)</p>
            <p>- <strong>Require User Input</strong> toggle: when enabled, users must fill this field in the plugin before submitting</p>
          </div>
          
          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Supported Data Types</p>
          <div className="ml-4 grid grid-cols-2 gap-x-4 gap-y-1">
            <p>- <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">text</code> Free text</p>
            <p>- <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">number</code> Numeric values</p>
            <p>- <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">currency</code> Monetary amounts</p>
            <p>- <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">percentage</code> Percentage values</p>
            <p>- <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">date</code> Date values</p>
            <p>- <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">boolean</code> Yes/No</p>
            <p>- <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">dropdown</code> Predefined options</p>
            <p>- <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">array_of_objects</code> Table (Line Items)</p>
          </div>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Table Fields (Line Items)</p>
          <p>• When you select "Table (Line Items)" as the data type, a <strong>Column Schema Builder</strong> appears:</p>
          <div className="ml-4 space-y-1">
            <p>- Define columns with: Column Label, key_name (auto-generated), Type (text/number/currency/date), Description</p>
            <p>- Add/remove columns dynamically</p>
            <p>- Perfect for repeating data like invoice line items (Description, Quantity, Unit Price, Amount)</p>
          </div>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Editing & Deleting</p>
          <p>• Click the <strong>pencil icon</strong> on any field to edit it in the same modal.</p>
          <p>• Click the <strong>trash icon</strong> to delete a single field.</p>
          <p>• Fields are displayed in their defined order with index numbers.</p>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Importing Fields</p>
          <p>• Click <strong>"Import Fields"</strong> to upload a JSON file. Supported formats:</p>
          <div className="ml-4 space-y-1">
            <p>- Azure CU format: <code className="text-[9px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{'{ fieldSchema: { fields: { ... } } }'}</code></p>
            <p>- Flat dict: <code className="text-[9px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{'{ fields: { FieldName: { type, description } } }'}</code></p>
            <p>- Direct dict: <code className="text-[9px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{'{ FieldName: { type, description } }'}</code></p>
          </div>
          <p>• You can choose to REPLACE existing fields or ADD alongside them.</p>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Building the Analyzer</p>
          <p>• Once fields are defined, click <strong>"Build Analyzer"</strong> to create a custom Azure Document Intelligence analyzer.</p>
          <p>• The analyzer must be built before the template can be used in the Outlook Plugin.</p>
          <p>• If you modify fields after building, a yellow banner appears: "Fields have changed - rebuild the analyzer for changes to take effect."</p>
          <p>• Click <strong>"Rebuild Now"</strong> in the banner, or use the "Build Analyzer" button again.</p>
          <p>• A navigation guard prevents you from leaving the page with unsaved changes (prompts confirmation).</p>
        </div>
      </section>

      {/* Section 5: Webhooks Tab */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">5</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Template Webhooks</h3>
        </div>
        <WebhooksTabMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mt-4">
          <p>The Webhooks tab lets you connect webhook endpoints to a template. When a request is approved/published for this template, the data is sent to all linked webhooks.</p>
          <p>• <strong>Connect a Webhook</strong> select from the dropdown of available organisation webhooks (created in the Webhooks page by admins) and click "Connect".</p>
          <p>• <strong>Disconnect</strong> remove a webhook from this template (with confirmation dialog). Admin-only.</p>
          <p>• <strong>Delivery Logs</strong> expand any linked webhook to see its delivery history:</p>
          <div className="ml-4 space-y-1">
            <p>- Status (success/failed), Event type, HTTP status code, Request ID, Timestamp, Duration</p>
            <p>- <strong>CSV Download</strong> link when the response contains a download URL</p>
            <p>- <strong>Retry</strong> button for failed deliveries (fires the webhook again)</p>
            <p>- Expandable error details for failed deliveries</p>
          </div>
          <p>• Non-admin users can view linked webhooks and delivery logs but cannot connect or disconnect.</p>
        </div>
      </section>

      {/* Section 6: Settings Tab */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">6</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Settings Tab</h3>
        </div>
        <SettingsTabMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mt-4">
          <p>The Settings tab shows template metadata and policy toggles:</p>
          
          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Template Info</p>
          <p>• Created by, Created date, Last updated (with modifier name), and Creation method (AI Suggested / Plugin Auto-created / Imported from JSON / Created Manually).</p>
          <p>• Source Documents section shows the files used to create/train this template.</p>
          <p>• AI Prompt Used (if template was created via AI suggestion).</p>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Duplicate Handling Toggle</p>
          <p>• <strong>ON (green)</strong> duplicates are allowed. The same file can be uploaded multiple times for this template.</p>
          <p>• <strong>OFF (red)</strong> duplicates are hard-blocked. The plugin will reject re-uploading the same file hash.</p>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Mandate Review Before Export Toggle</p>
          <p>• <strong>ON (green)</strong> requests go to "Reviewing" status after extraction. A user must manually review and publish before the webhook/CSV export fires.</p>
          <p>• <strong>OFF (gray)</strong> requests auto-approve after extraction and the webhook fires immediately.</p>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Manage Access</p>
          <p>• Control which users can see and use this template in the Outlook plugin. See Section 7 below for the full walkthrough.</p>
        </div>
      </section>

      {/* Section 7: Manage Access (expanded) */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">7</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Manage Access</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Control which users in your organisation can see and use this template in the Outlook Plugin. Only admins can manage access.
        </p>

        {/* Live Preview Mockup */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
          {/* Page header */}
          <div className="px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-gray-700 dark:text-gray-300" />
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Manage Access</span>
            </div>
          </div>

          {/* Column headers */}
          <div className="px-5 py-2 border-b border-gray-100 dark:border-gray-800">
            <div className="grid grid-cols-12 items-center text-[9px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-3">Job Title</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-1 text-right text-[8px] normal-case tracking-normal flex items-center justify-end gap-0.5">
                <span className="text-gray-400">↕</span>
                <span className="text-gray-500 dark:text-gray-400">Name A→Z</span>
              </div>
            </div>
          </div>

          {/* ADMINISTRATORS section */}
          <div className="px-5 pt-3 pb-1">
            <div className="flex items-center gap-2">
              <Shield className="h-3 w-3 text-pink-500" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-pink-500">Administrators</span>
              <span className="w-4 h-4 rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 text-[8px] font-bold flex items-center justify-center">2</span>
            </div>
          </div>

          {/* Admin rows */}
          <div className="divide-y divide-gray-50 dark:divide-gray-800/50">
            {[
              { initials: 'JD', color: 'bg-blue-100 text-blue-700', name: 'James Duncan', email: 'james.duncan@acme.com', title: 'Engineering Lead' },
              { initials: 'SP', color: 'bg-amber-100 text-amber-700', name: 'Sarah Palmer', email: 'sarah.palmer@acme.com', title: 'Product Manager' },
            ].map((user, i) => (
              <div key={i} className="grid grid-cols-12 items-center px-5 py-2">
                <div className="col-span-3 flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full ${user.color} flex items-center justify-center text-[8px] font-bold flex-shrink-0`}>{user.initials}</div>
                  <span className="text-[10px] font-medium text-gray-900 dark:text-gray-100 truncate">{user.name}</span>
                </div>
                <div className="col-span-3 text-[10px] text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                <div className="col-span-3 text-[10px] text-gray-500 dark:text-gray-400 truncate">{user.title}</div>
                <div className="col-span-3">
                  <span className="text-[9px] font-medium text-pink-600 dark:text-pink-400">Admin</span>
                </div>
              </div>
            ))}
          </div>

          {/* USERS section */}
          <div className="px-5 pt-4 pb-1 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <Users className="h-3 w-3 text-blue-500" />
              <span className="text-[9px] font-bold uppercase tracking-wider text-blue-500">Users</span>
              <span className="w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[8px] font-bold flex items-center justify-center">3</span>
            </div>
          </div>

          {/* User rows */}
          <div className="divide-y divide-gray-50 dark:divide-gray-800/50 pb-3">
            {[
              { initials: 'MR', color: 'bg-rose-100 text-rose-700', name: 'Michael Reed', email: 'michael.reed@acme.com', title: 'Software Engineer' },
              { initials: 'LT', color: 'bg-purple-100 text-purple-700', name: 'Lisa Tran', email: 'lisa.tran@acme.com', title: 'Data Analyst' },
              { initials: 'S', color: 'bg-emerald-100 text-emerald-700', name: 'Service Principal (a1b2c3d4...)', email: 'a1b2c3d4-...@app.entra', title: '-' },
            ].map((user, i) => (
              <div key={i} className="grid grid-cols-12 items-center px-5 py-2">
                <div className="col-span-3 flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full ${user.color} flex items-center justify-center text-[8px] font-bold flex-shrink-0`}>{user.initials}</div>
                  <span className="text-[10px] font-medium text-gray-900 dark:text-gray-100 truncate">{user.name}</span>
                </div>
                <div className="col-span-3 text-[10px] text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                <div className="col-span-3 text-[10px] text-gray-500 dark:text-gray-400 truncate">{user.title}</div>
                <div className="col-span-3">
                  <span className="text-[9px] font-medium text-gray-400 dark:text-gray-500">User</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Open Access (default):</strong> All users in the organisation can see and use this template. The toggle is ON (blue).</p>
          <p><strong>Restricted Access:</strong> Turn off the toggle to restrict. Only admins and explicitly selected users will have access.</p>
          <p><strong>Section grouping:</strong> Users are grouped into Administrators and Users with counts. Admins always retain access.</p>
          <p><strong>Sorting:</strong> Use the "Name A→Z" dropdown (top-right) to sort alphabetically or by role.</p>
          <p><strong>Service Principals:</strong> App registrations / managed identities appear as "Service Principal" with their client ID.</p>
        </div>
      </section>

      {/* Section 8: AI Field Suggestions */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">8</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">AI Field Suggestions</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Upload sample PDF documents and let AI analyse them to suggest extraction fields. Available on the Template Detail page (Fields tab).
        </p>

        {/* Upload Panel Mockup */}
        <div className="mt-2">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Upload Panel (Step 1)</p>
          <div className="rounded-xl border-2 border-purple-200 dark:border-purple-800/50 bg-gradient-to-b from-purple-50 to-white dark:from-purple-900/10 dark:to-gray-900 p-5 space-y-3 max-w-lg">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI Field Suggestion</span>
              <span className="text-[10px] text-gray-400 ml-1">Upload sample PDFs → AI generates template fields</span>
            </div>

            {/* Info banner */}
            <div className="flex items-start gap-2 rounded-lg border border-blue-200 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-900/20 px-3 py-2">
              <Info className="h-3.5 w-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-blue-700 dark:text-blue-300">
                Upload up to <strong>10 documents of the same type</strong>. The AI will analyze them together to discover extractable fields. Mixing different document types may produce inaccurate results.
              </p>
            </div>

            {/* Dropzone */}
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-5 text-center">
              <Upload className="h-6 w-6 text-gray-400 mx-auto mb-1.5" />
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Drop sample PDFs here or <span className="text-purple-600 dark:text-purple-400 font-medium">browse</span>
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">Upload up to 10 PDFs</p>
            </div>

            {/* Optional prompt */}
            <div>
              <label className="flex items-center gap-1 text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                <MessageSquare className="h-3 w-3" />
                Optional: Tell the AI what fields you're looking for
              </label>
              <textarea
                readOnly
                rows={2}
                placeholder="e.g., I need all financial fields like coupon rate, issue size, ISIN, maturity dates..."
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs resize-none dark:placeholder-gray-500"
              />
            </div>

            {/* Suggest button */}
            <button className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              Suggest Fields
            </button>
          </div>
        </div>

        {/* Processing State */}
        <div className="mt-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Processing State (Background Job)</p>
          <div className="rounded-lg border border-purple-200 dark:border-purple-800/50 bg-purple-50 dark:bg-purple-900/20 p-3 space-y-2 max-w-lg">
            <div className="flex items-center gap-2 text-xs text-purple-700 dark:text-purple-300">
              <RefreshCw className="h-3.5 w-3.5 animate-spin shrink-0" />
              <span className="font-medium">Analyzing document structure...</span>
            </div>
            <div className="ml-5">
              <div className="h-2 w-full rounded-full bg-purple-200 dark:bg-purple-800/50 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500" style={{ width: '45%' }} />
              </div>
            </div>
            <p className="text-[10px] text-purple-500 dark:text-purple-400 ml-5">You can navigate away - you'll be notified when ready.</p>
          </div>
        </div>

        {/* Review Panel Mockup */}
        <div className="mt-3">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Review Panel (Step 2 - After AI Completes)</p>
          <div className="rounded-xl border-2 border-purple-200 dark:border-purple-800/50 bg-gradient-to-b from-purple-50 to-white dark:from-purple-900/10 dark:to-gray-900 p-5 space-y-3 max-w-lg">
            {/* Document info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-medium text-gray-900 dark:text-gray-100">Insurance Policy Document</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Comprehensive property and casualty insurance policy with coverage details</p>
              <p className="text-[10px] text-gray-400 mt-1">sample_policy.pdf • 4/4 pages analyzed • 12 fields suggested</p>
            </div>

            {/* Select all / count */}
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-purple-600 dark:text-purple-400 font-medium">Select all</span>
              <span className="text-[10px] text-gray-400">8 of 12 selected</span>
            </div>

            {/* Suggested fields table (mini) */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-[10px]">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className="px-2 py-1.5 w-6"></th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-500">Field</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-500">Type</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-500">Category</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-500">Sample</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {[
                    { name: 'Policy Number', type: 'string', category: 'Identification', sample: 'POL-2024-001', selected: true },
                    { name: 'Effective Date', type: 'date', category: 'Dates', sample: '2024-01-15', selected: true },
                    { name: 'Premium Amount', type: 'currency', category: 'Financial', sample: '$2,450.00', selected: true },
                    { name: 'Coverage Limit', type: 'currency', category: 'Financial', sample: '$500,000', selected: false },
                  ].map((field, i) => (
                    <tr key={i} className={field.selected ? 'bg-purple-50 dark:bg-purple-900/10' : ''}>
                      <td className="px-2 py-1.5">
                        <div className={`w-3 h-3 rounded border-2 flex items-center justify-center ${field.selected ? 'border-purple-500 bg-purple-500' : 'border-gray-300 dark:border-gray-600'}`}>
                          {field.selected && <Check className="h-2 w-2 text-white" />}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 font-medium text-gray-900 dark:text-gray-100">{field.name}</td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1 py-0.5 rounded ${field.type === 'date' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : field.type === 'currency' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                          {field.type}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-500">{field.category}</td>
                      <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300 font-mono">{field.sample}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Accept button */}
            <div className="flex items-center gap-2">
              <button className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-xs font-medium">
                <Check className="h-3.5 w-3.5" />
                Accept 8 Selected Fields
              </button>
              <button className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-200">
                Discard
              </button>
            </div>
          </div>
        </div>

        <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Trigger:</strong> On the Template Detail page (Fields tab), click the purple <strong>"AI Suggest Fields"</strong> button in the toolbar.</p>
          <p><strong>Upload:</strong> Drag & drop or browse to upload 1-10 PDF sample documents of the same type.</p>
          <p><strong>Prompt (optional):</strong> Guide the AI with a natural language prompt (e.g. "Focus on financial terms and dates").</p>
          <p><strong>Background processing:</strong> After clicking "Suggest Fields", a background job runs with a purple progress bar. You can navigate away safely.</p>
          <p><strong>Review:</strong> When ready, a table shows suggested fields with name, type, category, sample value, and confidence. Select/deselect with checkboxes.</p>
          <p><strong>Accept:</strong> Click "Accept N Selected Fields" to add them to your template. Duplicates (same field_name) are automatically skipped and greyed out.</p>
        </div>
      </section>

      {/* Section 8: Complete Workflow */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">9</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Complete Workflow: Template to Extraction</h3>
        </div>
        <div className="space-y-3">
          {[
            { step: '1', title: 'Create Template', desc: 'Give it a name and description. Decide on reprocessing policy.' },
            { step: '2', title: 'Add Fields', desc: 'Define what data you want extracted. Use AI Suggest, manual Add Field, or Import from JSON.' },
            { step: '3', title: 'Configure Settings', desc: 'Set duplicate handling, mandate review policy, and user access control.' },
            { step: '4', title: 'Connect Webhooks', desc: 'Link webhook endpoints to automatically export data when requests are approved.' },
            { step: '5', title: 'Build Analyzer', desc: 'Click Build to create the Azure Document Intelligence analyzer from your fields.' },
            { step: '6', title: 'Use in Plugin', desc: 'Open the Outlook Plugin, select your template/analyzer, attach documents, and extract.' },
            { step: '7', title: 'Review Results', desc: 'Extracted data appears in the Requests page. Review, edit, and approve (or auto-approve if mandate review is off).' },
          ].map((item, i) => (
            <div key={i} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-purple-600 dark:text-purple-400">{item.step}</span>
                </div>
                {i < 6 && <div className="w-px flex-1 bg-purple-200 dark:bg-purple-800/40 mt-1" />}
              </div>
              <div className="pb-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tips */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">Good Field Descriptions = Better Extraction</span>
            </div>
            <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">The more descriptive your field descriptions are, the better the AI can identify and extract the right data. For example: "The total invoice amount including VAT, typically at the bottom of the document" is much better than just "total".</p>
          </div>
          <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-900 dark:text-green-200">Use AI Suggestions for New Document Types</span>
            </div>
            <p className="text-[11px] text-green-700 dark:text-green-300 leading-relaxed">When working with an unfamiliar document type, upload a sample and let AI suggest fields. It'll identify fields you might have missed and suggest appropriate data types.</p>
          </div>
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">Rebuild After Changes</span>
            </div>
            <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">If you add, remove, or modify fields after building the analyzer, you must rebuild for changes to take effect. The old analyzer continues working until the rebuild completes.</p>
          </div>
          <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Upload className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-900 dark:text-purple-200">Import from Azure Analyzer JSON</span>
            </div>
            <p className="text-[11px] text-purple-700 dark:text-purple-300 leading-relaxed">If you already have an Azure Custom Analyzer JSON definition, use "Import Fields" to import all field definitions at once instead of adding them manually one by one.</p>
          </div>
          <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span className="text-xs font-semibold text-red-900 dark:text-red-200">Deleting is Permanent</span>
            </div>
            <p className="text-[11px] text-red-700 dark:text-red-300 leading-relaxed">Deleting a template deactivates it, removes all fields, and unlinks the analyzer. This cannot be easily undone. Make sure to export any important configurations before deleting.</p>
          </div>
        </div>
      </section>

      {/* Related */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Related Modules</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'requests', label: 'Requests', icon: FileText },
            { id: 'webhooks', label: 'Webhooks', icon: Webhook },
            { id: 'email-queue', label: 'Email Queue', icon: Mail },
          ].map((mod) => {
            const Icon = mod.icon
            return (
              <button key={mod.label} onClick={() => onNavigate(mod.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <Icon className="h-3.5 w-3.5" />
                {mod.label}
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}

